// Debe ir primero: las librerías usan crypto.randomUUID al generar ids.
import 'src/polyfills';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { resourceReducer, ReducerRegistry, NotificationsManager } from '@twnel/web-components';
import { getApiHost, extendedAgentFields } from '@twnel/utils-js/web';
import { reducer as sessionReducer, LoginEnhancer } from '@twnel/companies-login';
import { fetchTexts } from 'src/data/actions';
import appReducer from 'src/data/reducers';
import makeThunkContext from 'src/data/socket';
import makeApp from 'src/ui/App';

// Env vars
const { npm_package_version: version, TWNEL_ENV, NODE_ENV } = process.env;
const environment = getApiHost() || TWNEL_ENV;
const debug = NODE_ENV !== 'production';

// Register reducers
const reducerRegistry = ReducerRegistry();
reducerRegistry.register({
  ...resourceReducer,
  ...sessionReducer,
  ...appReducer,
});

// Login setup: LoginEnhancer provides the thunk, session and company middleware, plus the
// preloaded session state.
const notificationsManager = NotificationsManager();
const { middleware: loginMiddleware, preloadedState } = LoginEnhancer({
  version,
  environment,
  thunkContext: (store) => {
    const thunkContext = makeThunkContext(store, environment, debug);
    return (company) => ({
      reducerRegistry,
      notificationsManager,
      ...thunkContext(company),
    });
  },
  options: {
    includeAgentOnlineStatus: true,
    includeAgentAutolockStatus: true,
    includeBusinessUnits: true,
    pickAgentFields: extendedAgentFields,
  },
  debug,
});

// Store setup. The login library provides the thunk middleware, and the session data is not
// serializable-checked.
const store = configureStore({
  reducer: combineReducers(reducerRegistry.getReducers()),
  middleware: (getDefault) => getDefault({ thunk: false, serializableCheck: false })
    .concat(...loginMiddleware),
  preloadedState,
  devTools: debug,
});

// Load the texts file
store.dispatch(fetchTexts());

// Initial render
const App = makeApp(store);
createRoot(document.getElementById('react')).render(React.createElement(App));
